import { useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash-es';
import { useDebounce } from '@namespace/helpers';
import { useSearchParams } from '@namespace/router';
import {
  MIN_GAMES_SEARCH_QUERY_LENGTH,
  PLEX_MOBILE_SEARCH_MAX_GAMES_PAGES_LIMIT
} from '../../constants';
import { useGamesStore } from '../../store';
import { getGames } from '../../utils';
import { useGetLoadFeedGamesCallback } from '../useGetLoadFeedGamesCallback';

export const useSearchGamesPlex = ({
  feedName,
  query = '',
  pageSize = 36,
  initialPageFromUrl = false
}) => {
  const games = useGamesStore('games');
  const { page: pageStr } = useSearchParams();
  const page = pageStr && initialPageFromUrl ? Number(pageStr) : undefined;
  const lowerQuery = query.toLowerCase();
  const debouncedQuery = useDebounce(lowerQuery, 500);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState({
    gamesIds: [],
    debouncedQuery: '',
    totalGames: undefined
  });
  const [isAllGamesLoaded, setIsAllGamesLoaded] = useState(false);
  const pageRef = useRef(0);
  const isInitialLoadDone = useRef(false);

  const currentQueryloadedGamesIds = useRef([]);

  const fetchGames = useGetLoadFeedGamesCallback();

  const loadGames = useCallback(
    async (searchQuery = '', isAllGamesLoadedArg) => {
      // if isAllGamesLoadedArg is passed - we should use it, no matter what state of isAllGamesLoaded
      // because when query is changed we set isAllGamesLoaded to false and immidiately call for loadGames function;
      // isAllGamesLoaded state might not been updated yet
      const isAllGamesLoadedFlag = isNil(isAllGamesLoadedArg)
        ? isAllGamesLoaded
        : isAllGamesLoadedArg;
      if (!searchQuery || searchQuery?.length < MIN_GAMES_SEARCH_QUERY_LENGTH) {
        setResult({
          gamesIds: [],
          debouncedQuery: searchQuery
        });

        return;
      }

      if (isLoading || isAllGamesLoadedFlag) {
        setResult((prev) => ({ ...prev, debouncedQuery: searchQuery }));
        return;
      }

      setIsLoading(true);

      // if initialized on a first (undefined) page - limit is pageSize
      // if initialized on more than 1st page - limit is pageSize * current page
      // if page is more than MAX - just call for a first page, url page reset is on the CasinoGamesSearch component's lvl
      const limit =
        !isInitialLoadDone.current &&
        page &&
        page > 1 &&
        page <= PLEX_MOBILE_SEARCH_MAX_GAMES_PAGES_LIMIT
          ? pageSize * page
          : pageSize;

      const searchResult = await fetchGames({
        feedName,
        filter: {
          name: searchQuery,
          limit,
          offset: pageRef.current * pageSize
        }
      });
      isInitialLoadDone.current = true;
      // TODO: replace totalGames with actual field from response when it will be implemented on BE
      const { gamesIds = [], totalGames, loadedGamesQty } = searchResult;
      const newGamesIds = [...currentQueryloadedGamesIds.current, ...gamesIds];
      currentQueryloadedGamesIds.current = [...newGamesIds];

      setResult({
        gamesIds: newGamesIds,
        debouncedQuery: searchQuery,
        totalGames
      });
      pageRef.current += limit / pageSize;
      setIsAllGamesLoaded(loadedGamesQty < limit);

      setIsLoading(false);
    },
    [feedName, fetchGames, isLoading, pageSize, result.gamesIds, page]
  );

  // reset on query change
  useEffect(() => {
    currentQueryloadedGamesIds.current = [];
  }, [debouncedQuery]);

  // initial load
  useEffect(() => {
    setIsAllGamesLoaded(false);
    pageRef.current = 0;
    loadGames(debouncedQuery, false);
  }, [debouncedQuery]);

  return {
    debouncedQuery: result.debouncedQuery,
    gamesResult: getGames(result.gamesIds, games.data),
    loadGames: () => loadGames(debouncedQuery),
    isLoading,
    totalGames: result.totalGames,
    isAllGamesLoaded
  };
};
